import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem, IconButton, Paper, useMediaQuery, FormControl, InputLabel } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import Navigation from '../../components/Navigation/Navigation';
import DeleteIcon from '@mui/icons-material/Delete';
import { FromStore } from '../../../helpers/Store';
import { ApiGetUsers, UserRegistered, UserRemoved, UserRoleUpdated } from '../../../stores/api/user/actions';
import FloatingActionButton from '../../components/FloatingActionButton/FloatingActionButton';
import AddUserDialog from './AddUserDialog';
import Button from '../../../PublicSide/components/Button/Button';
import DeleteConfirmationDialog from '../../components/DeleteConfirmation/DeleteConfirmationDialog';
import { CenteredContainer, StyledTableContainerServersUsersManagement, TypographyEllipsisStyled } from '../../../style/GlobalsComponentsStyled';
import { HandleError, HandleSuccess, ShowErrorMessage } from '../../../stores/page/actions';
import ServiceLocator from '../../../services/ServiceLocator';
import { WithRefreshToken } from '../../../stores/auth/actions';
import ErrorCode from '../../../api/dtos/ErrorCode';
import { useAppDispatch } from '../../../helpers/UseAppDispatch';
import { UserInfo } from '../../../api/dtos/user/UserProfile';
import { UserRoleResponse } from '../../../api/dtos/user/UserRoleResponse';
import { UpdateRoleRequest } from '../../../api/dtos/user/UpdateRoleRequest';

const StyledBoxContainer = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(to top right, #CDC7E5 0%, #A92FFB 28%, #891CFB 59%, #2F27CE 100%)',
    padding: '2px',
    borderRadius: theme.spacing(1),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const StyledUserCard = styled(Paper)(({ theme }) => ({
    padding: '1.5rem',
    borderRadius: theme.spacing(1),
    background: '#1C1D2B',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center'
}));

const StyledTableCell = styled(TableCell)({
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '110px'

});

const StyledSelect = styled(Select)(({ theme }) => ({
    backgroundColor: '#2A2D3E',
    borderRadius: theme.spacing(1),
    padding: '0.5rem',
    '& .MuiSvgIcon-root': {
        color: 'white',
    },
    '& .MuiSelect-nativeInput': {
        visibility: 'hidden',
    }
}));

const UserManagementPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const users: UserInfo[] = FromStore(state => state.apis.profile.users);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [userToDelete, setUserToDelete] = useState<UserInfo | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const getAllUsers = async () => {
        await dispatch(ApiGetUsers());
    };

    useEffect(() => {
        getAllUsers();
    }, [])

    const handleRoleChange = async (userId: number, newRole: UserRoleResponse) => {
        const request: UpdateRoleRequest = { role: newRole };
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ManagementService.UpdateUserRole(userId, request);
            if (response.is_success) {
                dispatch(UserRoleUpdated(userId, response.data));
                getAllUsers();
                dispatch(HandleSuccess('User role updated successfully.'));
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleDeleteUser = async (userId: number) => {
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ManagementService.RemoveUser(userId);
            if (response.is_success) {
                dispatch(UserRemoved(userId));
                setIsDeleteDialogOpen(false);
                getAllUsers();
                dispatch(HandleSuccess('User deleted successfully.'));
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleAddUser = async (email: string, role: UserRoleResponse) => {
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ManagementService.Register({
                email: email,
                role: role
            });
            if (response.is_success) {
                dispatch(UserRegistered(email, role));
                getAllUsers();
                dispatch(HandleSuccess(`User with email ${email} added successfully.`))
                setIsDialogOpen(false);
            }
            else if (response.error_code === ErrorCode.INVALID_FORMAT_EMAIL) {
                dispatch(ShowErrorMessage(response.error_message ?? 'The email format is invalid'));
            }
            else if (response.error_code === ErrorCode.EMAIL_ALREADY_EXISTS) {
                dispatch(ShowErrorMessage(response.error_message ?? 'This email is already registered, please use another email.'));
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    return (
        <>
            <Navigation />
            <CenteredContainer className='main_content'>
                <Typography variant='h2'>Manage Users</Typography>
                {isDesktop ? (
                    <>
                        <StyledTableContainerServersUsersManagement>
                            <Paper sx={{ backgroundColor: '#1C1D2B' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Name</StyledTableCell>
                                            <StyledTableCell>Email</StyledTableCell>
                                            <StyledTableCell>Role</StyledTableCell>
                                            <StyledTableCell>Delete</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user: any) => (
                                            <TableRow key={user.id}>
                                                <StyledTableCell>{user.name}</StyledTableCell>
                                                <StyledTableCell>{user.email}</StyledTableCell>
                                                <StyledTableCell>
                                                    {/* FormControl for accessibility with InputLabel etc */}
                                                    <FormControl sx={{ width: '130px' }}>
                                                        <InputLabel id={`user-role-label-${user.id}`} sx={{ visibility: 'hidden' }}>User Role</InputLabel>
                                                        <StyledSelect
                                                            labelId={`user-role-label-${user.id}`}
                                                            value={user.role}
                                                            onChange={(e) => handleRoleChange(user.id, e.target.value as UserRoleResponse)}
                                                            inputProps={{ 'aria-labelledby': `user-role-label-${user.id}` }}
                                                            MenuProps={{ PaperProps: { sx: { backgroundColor: '#2A2D3E' } } }}
                                                        >
                                                            <MenuItem value="Admin">Admin</MenuItem>
                                                            <MenuItem value="Employee">Employee</MenuItem>
                                                        </StyledSelect>
                                                    </FormControl>
                                                    {/* <StyledSelect value={user.role} onChange={(e) => handleRoleChange(user.id, e.target.value as UserRoleResponse)} label='User Role' aria-label='Select user role'>
                                                        <MenuItem value='Admin'>Admin</MenuItem>
                                                        <MenuItem  value='Employee'>Employee</MenuItem>
                                                    </StyledSelect> */}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <IconButton onClick={() => { setUserToDelete(user); setIsDeleteDialogOpen(true); }} color='error' aria-label='delete user'>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </StyledTableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </StyledTableContainerServersUsersManagement>
                    </>
                ) : (
                    <>
                        {users.map((user) => (
                            <StyledBoxContainer key={user.id}>
                                <StyledUserCard>
                                    <TypographyEllipsisStyled>{user.name}</TypographyEllipsisStyled>
                                    <TypographyEllipsisStyled >{user.email}</TypographyEllipsisStyled>
                                    <FormControl sx={{ width: '130px' }}>
                                        <InputLabel id={`user-role-label-${user.id}`} sx={{ visibility: 'hidden' }}>User Role</InputLabel>
                                        <StyledSelect
                                            labelId={`user-role-label-${user.id}`}
                                            value={user.role}
                                            onChange={(e) => { user.id && handleRoleChange(user.id, e.target.value as UserRoleResponse) }}
                                            inputProps={{ 'aria-labelledby': `user-role-label-${user.id}` }}
                                            MenuProps={{ PaperProps: { sx: { backgroundColor: '#2A2D3E' } } }}
                                        >
                                            <MenuItem value="Admin">Admin</MenuItem>
                                            <MenuItem value="Employee">Employee</MenuItem>
                                        </StyledSelect>
                                    </FormControl>
                                    {/* <StyledSelect value={user.role} onChange={(e) => { user.id && handleRoleChange(user.id, e.target.value as UserRoleResponse) }} label='User Role' aria-label='Select user role'>
                                        <MenuItem value='Admin'>Admin</MenuItem>
                                        <MenuItem value='Employee'>Employee</MenuItem>
                                    </StyledSelect> */}
                                    <Button text='Delete' onClick={() => { user.id && handleDeleteUser(user.id) }} />
                                </StyledUserCard>
                            </StyledBoxContainer>
                        ))}
                    </>
                )}

                <FloatingActionButton onClick={() => setIsDialogOpen(true)} label='Add User' />

                <AddUserDialog
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    onSubmit={handleAddUser}
                />
                <DeleteConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={() => handleDeleteUser(userToDelete?.id as number)}
                />
            </CenteredContainer>
        </>
    );
};

export default UserManagementPage;

